import type { EntityType } from "@config";
import { extractCellText } from "@library-elements/table/utils/extract-cell-text";
import type { ITableCell } from "@library-elements/table/types";
import { nanoid } from "@reduxjs/toolkit";
import type { StoryStateType } from "../types";
import { resolveAbsoluteIndex } from "./apply-in-cell-element-move";

// Snapshot of a text→RichText promotion applied during an in-cell move so
// undo/broadcast/Yjs can reverse or replay it as part of the same step.
export interface PromoteCellPlainTextResult {
  richTextId: string;
  entities: Record<string, EntityType>;
  rootIds: string[];
  content: string;
  priorContent: string;
  // Absolute splice index into table.childIds where the RichText was inserted.
  index: number;
  tableData: ITableCell[];
}

/**
 * When a cell still holds native `content` text, adopt it as a RichText child
 * at the front of that cell's stack and clear `content` — same shape as the
 * first `insert-into-cell` conversion. No-op when there is no plain text.
 *
 * Used by move-into-cell so the former text band becomes a real reorderable
 * sibling before the moved element is spliced.
 */
export const promoteCellPlainText = (
  state: Pick<StoryStateType, "flatContentLayers">,
  tableId: string,
  cellId: string,
): PromoteCellPlainTextResult | undefined => {
  const table = state.flatContentLayers[tableId];
  if (!table) return undefined;
  const tableData = table.settings.tableData as ITableCell[] | undefined;
  const cell = tableData?.find((c) => c.id === cellId && !c.mergedInto);
  if (!cell) return undefined;
  const priorContent = cell.content ?? "";
  if (extractCellText(priorContent).length === 0) return undefined;

  const richTextId = nanoid();
  const richText: EntityType = {
    id: richTextId,
    type: "element",
    parentId: tableId,
    childIds: [],
    settings: { name: "RichText", content: priorContent, cellId },
  };

  state.flatContentLayers[richTextId] = richText;
  cell.content = "";

  const index = resolveAbsoluteIndex(
    state.flatContentLayers,
    table.childIds,
    cellId,
    0,
  );
  table.childIds.splice(index, 0, richTextId);

  return {
    richTextId,
    entities: { [richTextId]: richText },
    rootIds: [richTextId],
    content: "",
    priorContent,
    index,
    tableData: tableData as ITableCell[],
  };
};

/** Apply a previously-broadcast promotion on a receiving peer (no new ids). */
export const applyPromotedCellPlainText = (
  state: Pick<StoryStateType, "flatContentLayers">,
  args: {
    tableId: string;
    cellId: string;
    entities: Record<string, EntityType>;
    rootIds: string[];
    index: number;
    content: string;
  },
): void => {
  const table = state.flatContentLayers[args.tableId];
  if (!table || !args.rootIds.length) return;
  const tableData = table.settings.tableData as ITableCell[] | undefined;
  const cell = tableData?.find((c) => c.id === args.cellId && !c.mergedInto);
  if (!cell) return;

  Object.assign(state.flatContentLayers, args.entities);
  const clamped = Math.max(0, Math.min(args.index, table.childIds.length));
  table.childIds.splice(clamped, 0, ...args.rootIds);
  cell.content = args.content;
};
